import React, {useEffect, useLayoutEffect, useState} from "react";
import {CreditCard, PaymentForm} from "react-square-web-payments-sdk";
import { SQUARE_APP_ID, SQUARE_LOCATION_ID } from "../../squareConfig";
import {
	Container,
	Row,
	Col,
	Modal,
} from "react-bootstrap";
import api from "../../api";
import {useAuth} from "../../AuthUser";
import {useNavigate, Navigate, useLocation, useParams} from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import MoneyBackImg from "../../assets/img/90day.png";
import API from "../../api";
import { trackEvent, trackEventOnce } from "../../analytics";
import { buildVerificationDetails, useSquareVerificationGuard } from "./squareSCA";

function useQuery() {
	const { search } = useLocation();

	return React.useMemo(() => new URLSearchParams(search), [search]);
}

const Plan = () => {
	const [show, setShow] = useState(false);
	const handleClose = () => setShow(false);
	const handleShow = () => setShow(true);
	const [plan, setPlan] = useState([])
	// Prorated refund preview for upgrades (MGDWD-229): the buyer must see the
	// amount BEFORE paying, so the backend computes it from Square and we only
	// render it. null = anonymous / not an upgrade / preview unavailable.
	const [upgradePreview, setUpgradePreview] = useState(null);

	const { setUser, user } = useAuth();
	const navigate = useNavigate();
	let query = useQuery();
	let { planId } = useParams();
	const offerCode = query.get("code");

	useEffect(() => {
		setTimeout(() => {
			API.get(`plans/${planId}`)
				.then(response => {
					setPlan(response.data);
					if (response.data?.amount) {
						trackEventOnce(`begin_checkout:${response.data.sourceId}`, "begin_checkout", {
							currency: "USD",
							value: response.data.amount / 100,
							items: [{ item_id: response.data.sourceId, item_name: response.data.planName }],
						});
					}
					return response.data;
				})

		}, 0);
	}, [planId])

	useEffect(() => {
		// Anonymous visitors can reach /plans/:planId (no RequireAuth) — only
		// authed users can have something to upgrade from. Failures just hide
		// the line: the preview is informational, never a checkout blocker.
		if (!user) return;
		api.get("pay/upgrade-preview")
			.then(resp => setUpgradePreview(resp.data))
			.catch(() => setUpgradePreview(null));
	}, [user, planId])


	const notify = (message) =>
		toast.error(message, {
			position: toast.POSITION.TOP_RIGHT,
		});

	// One shape for every way a payment can die, so the GA4 report can be read
	// as "attempts vs failures by reason" without reconciling three variants.
	const reportPaymentFailed = (reason) =>
		trackEvent("payment_failed", {
			reason,
			currency: "USD",
			value: (plan?.amount ?? 0) / 100,
			items: [{ item_id: plan?.sourceId, item_name: plan?.planName }],
		});

	// The guard's two exits are the only failures that never reach
	// cardTokenizeResponseReceived: the SDK swallows a cancelled or failed 3-D
	// Secure challenge. That is exactly the decline Chris reported from a
	// German buyer, and without this it would be invisible in the funnel.
	const { startGuard, clearGuard } = useSquareVerificationGuard({
		notify,
		onFailure: reportPaymentFailed,
	});

const cadenceMap = {
	'MONTHLY': 'month',
	'ANNUAL': 'annual',
}

function insertDecimal(num) {
	return Number((num / 100).toFixed(2));
}

const cadence = cadenceMap[plan.cadence];

	return (
		<Container>
			<Row>
				<Col sm={12} md={{ span: 6, offset: 3 }} lg={{ span: 6, offset: 3 }} xl={{ span: 6, offset: 3 }}>
					<Row className="align-items-center mt-4">
						<Col><h2 className="">{plan.planName} Membership</h2>
						</Col>
					</Row>
					<Row className="align-items-center">
					<Col>
						<div className="d-grid gap-2 mt-4 mb-4">
							<div className="plan-description" dangerouslySetInnerHTML={{__html: plan.description}}>
							</div>
							{!!plan.amount && (
								<div className="d-flex align-items-center gap-1">
									<div className="h1">
										${insertDecimal(plan.amount)}
									</div>
									<small className="">/{cadence}</small>
								</div>
							)}
							{/* MGDWD-229: show the prorated refund before the buyer pays.
							Gated on the backend's answer (authoritative — same math the
							refund will use), plus planId mismatch so the line never shows
							when someone lands on their own plan's page. Kept OUTSIDE the
							onClickCapture={startGuard} wrapper below on purpose. */}
							{upgradePreview?.upgrade && upgradePreview.credit > 0
								&& user?.planId !== planId && (
								<div className="alert alert-success py-2 px-3 mb-0" role="note">
									Upgrading from {upgradePreview.currentPlan?.planName || "your current plan"}?
									{" "}We&apos;ll refund <strong>${(upgradePreview.credit / 100).toFixed(2)}</strong> to
									your card for your {upgradePreview.unusedDays} unused{" "}
									{upgradePreview.unusedDays === 1 ? "day" : "days"} as soon as the upgrade completes.
								</div>
							)}

						</div>
					</Col>
					</Row>
					{/* Payment form only once the plan is loaded: an unknown or
					still-loading plan must not accept a card submission. */}
					{!!plan.amount && <PaymentForm
						applicationId={SQUARE_APP_ID}
						createVerificationDetails={() => buildVerificationDetails({ user })}
						cardTokenizeResponseReceived={async (token, verifiedBuyer) => {
							// Before any early return: the SCA guard must be disarmed the
							// moment the callback lands, or a failed tokenization would still
							// pop the "verification didn't complete" toast 5s later.
							clearGuard();
							// Square hands us a token only when tokenization succeeded; a
							// declined card fails later, inside /pay/create. Both branches are
							// reported so the funnel shows attempts and declines, not just wins.
							if (token?.status !== "OK" || !token?.token) {
								reportPaymentFailed(token?.errors?.[0]?.code || "tokenize_error");
								return;
							}
							trackEvent("add_payment_info", {
								currency: "USD",
								value: (plan?.amount ?? 0) / 100,
								payment_type: "card",
								items: [{ item_id: plan?.sourceId, item_name: plan?.planName }],
							});
							handleShow();
							await api.post("/pay/create", {
								sourceId: token.token,
								verificationToken: verifiedBuyer?.token,
								planId,
								offerCode,
								}).then(resp => {
									// resp.data = { sub, plan } from createPayment; sub.sourceId is the
									// Square subscription id — a natural unique transaction_id.
									trackEvent("purchase", {
										transaction_id: resp?.data?.sub?.sourceId,
										currency: "USD",
										value: (plan?.amount ?? 0) / 100,
										items: [{ item_id: plan?.sourceId, item_name: plan?.planName }],
									});
									handleClose();
									navigate("/thankyou", { replace: true });
							}).catch(err => {
								handleClose();
								// Square's decline detail, when the API forwards one; the HTTP
								// status is the stable fallback. Never the toast text — that is
								// user-facing copy and would fragment the report.
								reportPaymentFailed(
									err?.response?.data?.code
										|| (err?.response?.status ? `http_${err.response.status}` : "network_error"),
								);
								// err.response is absent on network-level failures — without
								// the fallback the user got a silent close and no toast.
								notify(err.response?.data?.message || "Something went wrong processing your payment. Please try again.");
							})
						}}
						locationId={SQUARE_LOCATION_ID}
					>
						{/* onClickCapture, not buttonProps.onClick: the SDK's own click
						handler calls stopPropagation() before React's synthetic event
						system sees it, so an onClick passed via buttonProps never fires.
						Capture phase runs before that native handler does. */}
						<div onClickCapture={startGuard}>
							<CreditCard
								buttonProps={{
									css: {
										backgroundColor: "#157729",
										fontSize: "14px",
										color: "#fff",
										"&:hover": {
											backgroundColor: "#1f8c44",
										},
									},
								}}
							>Start Your Membership Now</CreditCard>
						</div>
					</PaymentForm>}
					<div className="mt-4 text-black-50">
						<div dangerouslySetInnerHTML={{__html: plan.terms}}>
						</div>
					</div>
				</Col>
				</Row>
			<ToastContainer/>
			<Modal
				show={show}
				onHide={handleClose}
				backdrop="static"
				keyboard={false}
			>
				<Modal.Body>
					<div className="text-center">
						<p>Processing Payment...</p>
						<div className="spinner-border" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
					</div>
				</Modal.Body>
			</Modal>
		</Container>)
}

export default Plan;
